import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, Users, Building, Clock, MessageSquare, Home, CreditCard, Calendar, Megaphone, CheckCircle, Receipt, Ban } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from 'recharts';
import { Card, StatCard, Badge, Loading, Button } from '../components/ui';
import { dashboardService, departamentosService, comunicadosService, reservasService, sancionesService, egresosService } from '../services/api';
import { formatMoney, formatDate } from '../utils/formatters';
import { MESES } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// Dashboard para Propietario
const PropietarioDashboard = ({ usuario }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [sanciones, setSanciones] = useState({ total_pendiente: 0, cantidad_pendientes: 0 });
  const [comunicados, setComunicados] = useState([]);
  const [misReservas, setMisReservas] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const promises = [comunicadosService.getAll({ limite: 3 })];

      if (usuario?.departamento_id) {
        promises.push(departamentosService.getEstadoCuenta(usuario.departamento_id));
        promises.push(sancionesService.getByDepartamento(usuario.departamento_id));
      }

      const results = await Promise.all(promises);
      setComunicados(results[0].data.data || []);
      if (results[1]) {
        setEstadoCuenta(results[1].data.data);
      }
      if (results[2]) {
        setSanciones(results[2].data.data || { total_pendiente: 0, cantidad_pendientes: 0 });
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading size="lg" text="Cargando..." />;

  const deudaRecibos = estadoCuenta?.deuda_total || 0;
  const deudaSanciones = sanciones?.total_pendiente || 0;
  const deudaTotal = deudaRecibos + deudaSanciones;
  const mesesPendientes = estadoCuenta?.meses_pendientes || 0;
  const sancionesPendientes = sanciones?.cantidad_pendientes || 0;
  const ultimoPago = estadoCuenta?.ultimo_pago;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">¡Hola, {usuario?.nombre?.split(' ')[0]}!</h1>
        <p className="text-slate-400">Bienvenido a tu portal de propietario</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <Home className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Mi Departamento</p>
              <p className="text-2xl font-bold text-white">{usuario?.departamento_numero || '-'}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${deudaTotal > 0 ? 'bg-rose-500/20' : 'bg-emerald-500/20'}`}>
              {deudaTotal > 0 ? (
                <AlertTriangle className="text-rose-400" size={24} />
              ) : (
                <CheckCircle className="text-emerald-400" size={24} />
              )}
            </div>
            <div>
              <p className="text-sm text-slate-400">Deuda Total</p>
              <p className={`text-2xl font-bold ${deudaTotal > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {formatMoney(deudaTotal)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${mesesPendientes > 0 ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`}>
              <Clock className={mesesPendientes > 0 ? 'text-amber-400' : 'text-emerald-400'} size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Recibos Pend.</p>
              <p className={`text-2xl font-bold ${mesesPendientes > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {mesesPendientes}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${sancionesPendientes > 0 ? 'bg-rose-500/20' : 'bg-emerald-500/20'}`}>
              <Ban className={sancionesPendientes > 0 ? 'text-rose-400' : 'text-emerald-400'} size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Sanciones</p>
              <p className={`text-2xl font-bold ${sancionesPendientes > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {sancionesPendientes}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-500/20">
              <CreditCard className="text-purple-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Último Pago</p>
              <p className="text-lg font-bold text-white">
                {ultimoPago ? formatDate(ultimoPago) : 'Sin pagos'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Alerta de deuda */}
      {deudaTotal > 0 && (
        <Card className="p-4 border-amber-500/50 bg-amber-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-amber-400" size={24} />
              <div>
                <p className="text-amber-400 font-medium">
                  Tienes {mesesPendientes > 0 ? `${mesesPendientes} recibo(s)` : ''}
                  {mesesPendientes > 0 && sancionesPendientes > 0 ? ' y ' : ''}
                  {sancionesPendientes > 0 ? `${sancionesPendientes} sanción(es)` : ''} pendiente(s)
                </p>
                <p className="text-slate-400 text-sm">Total a pagar: {formatMoney(deudaTotal)}</p>
              </div>
            </div>
            <Button onClick={() => navigate('/mis-pagos')}>Pagar Ahora</Button>
          </div>
        </Card>
      )}

      {/* Contenido */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Comunicados Recientes */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Megaphone size={18} className="text-blue-400" />
              Comunicados Recientes
            </h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/comunicados')}>Ver todos</Button>
          </div>
          {comunicados.length > 0 ? (
            <div className="space-y-3">
              {comunicados.map((com, i) => (
                <div key={i} className="p-3 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">{com.titulo}</p>
                      <p className="text-slate-500 text-xs mt-1">{formatDate(com.created_at)}</p>
                    </div>
                    <Badge variant={com.tipo === 'URGENTE' ? 'danger' : 'info'} size="sm">
                      {com.tipo}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No hay comunicados recientes</p>
          )}
        </Card>

        {/* Accesos Rápidos */}
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4">Accesos Rápidos</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/mis-pagos')}
              className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 hover:from-emerald-500/30 hover:to-teal-500/30 border border-emerald-500/30 transition-colors text-left"
            >
              <Receipt className="text-emerald-400 mb-2" size={24} />
              <p className="text-white font-medium">Mis Pagos</p>
              <p className="text-slate-500 text-xs">Pagar cuotas</p>
            </button>
            <button
              onClick={() => navigate('/mi-departamento')}
              className="p-4 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 transition-colors text-left"
            >
              <Home className="text-blue-400 mb-2" size={24} />
              <p className="text-white font-medium">Mi Departamento</p>
              <p className="text-slate-500 text-xs">Estado de cuenta</p>
            </button>
            <button
              onClick={() => navigate('/reservas')}
              className="p-4 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 transition-colors text-left"
            >
              <Calendar className="text-purple-400 mb-2" size={24} />
              <p className="text-white font-medium">Reservar Área</p>
              <p className="text-slate-500 text-xs">Áreas comunes</p>
            </button>
            <button
              onClick={() => navigate('/incidencias')}
              className="p-4 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 transition-colors text-left"
            >
              <MessageSquare className="text-amber-400 mb-2" size={24} />
              <p className="text-white font-medium">Reportar</p>
              <p className="text-slate-500 text-xs">Incidencias</p>
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Dashboard para Administración
const AdminDashboard = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [miEstadoCuenta, setMiEstadoCuenta] = useState(null);
  const [historicoServicios, setHistoricoServicios] = useState({ datos: [], servicios: [] });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const promises = [
        dashboardService.getDatos(),
        egresosService.getHistoricoServicios({ meses: 12 })
      ];

      // Si el admin también es propietario, cargar su estado de cuenta
      if (usuario?.departamento_id) {
        promises.push(departamentosService.getEstadoCuenta(usuario.departamento_id));
      }

      const results = await Promise.all(promises);
      setData(results[0].data.data);

      // Debug
      console.log('Respuesta servicios completa:', results[1]);
      console.log('Datos servicios:', results[1].data);

      // La respuesta puede venir en results[1].data.data o directamente en results[1].data
      const serviciosData = results[1].data.data || results[1].data;
      console.log('Servicios procesados:', serviciosData);
      setHistoricoServicios(serviciosData || { datos: [], servicios: [] });

      if (results[2]) {
        setMiEstadoCuenta(results[2].data.data);
      }
    } catch (error) {
      console.error('Error cargando dashboard:', error);
      toast.error('Error al cargar el dashboard');
    } finally {
      console.log('Dashboard cargado, loading = false');
      setLoading(false);
    }
  };

  if (loading) return <Loading size="lg" text="Cargando dashboard..." />;
  if (!data) return null;

  const { resumen, actividad, graficos, alertas, morosos } = data;
  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
  const miDeuda = miEstadoCuenta?.deuda_total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400">Resumen general del edificio</p>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          icon={DollarSign}
          title="Saldo Disponible"
          value={formatMoney(resumen.saldoDisponible)}
          color="emerald"
        />
        <StatCard
          icon={TrendingUp}
          title="Ingresos del Mes"
          value={formatMoney(resumen.totalIngresos)}
          color="blue"
        />
        <StatCard
          icon={TrendingDown}
          title="Egresos del Mes"
          value={formatMoney(resumen.totalEgresos)}
          color="rose"
        />
        <StatCard
          icon={AlertTriangle}
          title="Morosidad Total"
          value={formatMoney(resumen.morosidadTotal)}
          subtitle={`${resumen.departamentosMorosos} departamento(s)`}
          color="amber"
        />
      </div>

      {/* Alertas */}
      {alertas && alertas.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-400" />
            Alertas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {alertas.map((alerta, i) => (
              <div
                key={i}
                className={`p-3 rounded-xl ${alerta.tipo === 'warning'
                  ? 'bg-amber-500/10 border border-amber-500/20'
                  : 'bg-blue-500/10 border border-blue-500/20'
                  }`}
              >
                <p className={`text-sm ${alerta.tipo === 'warning' ? 'text-amber-400' : 'text-blue-400'}`}>
                  {alerta.mensaje}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}


      {/* Recibos por Vencer y Egresos por Pagar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recibos por Vencer (Cuentas por Cobrar) */}
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-400" />
            Cuentas por Cobrar
          </h3>
          <p className="text-xs text-slate-400 mb-4">Recibos de propietarios por vencer</p>
          {data.recibosPorVencer && data.recibosPorVencer.length > 0 ? (
            <div className="space-y-3">
              {data.recibosPorVencer.map((recibo, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${recibo.dias_restantes < 3 ? 'bg-rose-500/20' :
                        recibo.dias_restantes < 7 ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                      }`}>
                      <span className={`font-bold text-sm ${recibo.dias_restantes < 3 ? 'text-rose-400' :
                          recibo.dias_restantes < 7 ? 'text-amber-400' : 'text-emerald-400'
                        }`}>
                        {recibo.departamento}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{recibo.propietario}</p>
                      <p className="text-xs text-slate-500">
                        {MESES.find(m => m.value === recibo.mes)?.label} {recibo.anio}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium text-sm">{formatMoney(recibo.monto)}</p>
                    <p className={`text-xs font-medium ${recibo.dias_restantes < 3 ? 'text-rose-400' :
                        recibo.dias_restantes < 7 ? 'text-amber-400' : 'text-emerald-400'
                      }`}>
                      {recibo.dias_restantes === 0 ? 'Hoy' :
                        recibo.dias_restantes === 1 ? 'Mañana' :
                          `${recibo.dias_restantes} días`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Sin recibos por vencer</p>
          )}
        </Card>

        {/* Egresos por Pagar (Cuentas por Pagar) */}
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingDown size={18} className="text-rose-400" />
            Cuentas por Pagar
          </h3>
          <p className="text-xs text-slate-400 mb-4">Facturas y egresos del edificio</p>
          {data.egresosPorPagar && data.egresosPorPagar.length > 0 ? (
            <div className="space-y-3">
              {data.egresosPorPagar.map((egreso, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${egreso.dias_restantes < 3 ? 'bg-rose-500/20' :
                        egreso.dias_restantes < 7 ? 'bg-amber-500/20' : 'bg-blue-500/20'
                      }`}>
                      <span className={`text-lg ${egreso.dias_restantes < 3 ? 'text-rose-400' :
                          egreso.dias_restantes < 7 ? 'text-amber-400' : 'text-blue-400'
                        }`}>
                        {egreso.categoria === 'PERSONAL' ? '👤' :
                          egreso.categoria === 'GASTO_FIJO' ? '📌' :
                            egreso.categoria === 'GASTO_VARIABLE' ? '📊' : '💰'}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{egreso.concepto}</p>
                      <p className="text-xs text-slate-500">{egreso.proveedor}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium text-sm">{formatMoney(egreso.monto)}</p>
                    <p className={`text-xs font-medium ${egreso.dias_restantes < 3 ? 'text-rose-400' :
                        egreso.dias_restantes < 7 ? 'text-amber-400' : 'text-blue-400'
                      }`}>
                      {egreso.dias_restantes === 0 ? 'Hoy' :
                        egreso.dias_restantes === 1 ? 'Mañana' :
                          `${egreso.dias_restantes} días`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Sin egresos por pagar</p>
          )}
        </Card>
      </div>

      {/* Gráficos de Servicios - Fila 1: Básicos y Mantenimiento */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">💧 Servicios Básicos</h3>
          <p className="text-xs text-slate-400 mb-3">Agua y Luz</p>
          {historicoServicios.datos.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={historicoServicios.datos}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="mes" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} labelStyle={{ color: '#f1f5f9' }} formatter={(value) => formatMoney(value)} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="AGUA" fill="#3b82f6" name="Agua" />
                <Bar dataKey="LUZ" fill="#f59e0b" name="Luz" />
              </BarChart>
            </ResponsiveContainer>
          ) : (<div className="flex items-center justify-center h-64 text-slate-400"><p className="text-sm">Sin datos</p></div>)}
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">🛠️ Servicios de Mantenimiento</h3>
          <p className="text-xs text-slate-400 mb-3">Limpieza y Seguridad</p>
          {historicoServicios.datos.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={historicoServicios.datos}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="mes" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} labelStyle={{ color: '#f1f5f9' }} formatter={(value) => formatMoney(value)} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="LIMPIEZA" fill="#10b981" name="Limpieza" />
                <Bar dataKey="SEGURIDAD" fill="#f97316" name="Seguridad" />
              </BarChart>
            </ResponsiveContainer>
          ) : (<div className="flex items-center justify-center h-64 text-slate-400"><p className="text-sm">Sin datos</p></div>)}
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Flujo de Caja */}
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4">Flujo de Caja - Últimos 6 Meses</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={graficos?.flujoCaja || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="mes" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `S/${v / 1000}k`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  formatter={(value) => [`S/ ${value.toLocaleString()}`, '']}
                />
                <Line type="monotone" dataKey="ingresos" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} name="Ingresos" />
                <Line type="monotone" dataKey="egresos" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444' }} name="Egresos" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Distribución de Egresos */}
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4">Distribución de Egresos</h3>
          <div className="h-72 flex items-center">
            {graficos?.distribucionEgresos && graficos.distribucionEgresos.length > 0 ? (
              <>
                <ResponsiveContainer width="60%" height="100%">
                  <PieChart>
                    <Pie
                      data={graficos.distribucionEgresos}
                      dataKey="monto"
                      nameKey="categoria"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {graficos.distribucionEgresos.map((entry, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      formatter={(value) => [`S/ ${value.toLocaleString()}`, '']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {graficos.distribucionEgresos.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        <span className="text-slate-400">{item.categoria}</span>
                      </div>
                      <span className="text-white font-medium">{item.porcentaje}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-slate-500 text-center w-full">Sin egresos este mes</p>
            )}
          </div>
        </Card>
      </div>

      {/* Actividad y Morosos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Actividad del día */}
        <Card className="p-6">
          <h3 className="font-semibold text-white mb-4">Actividad</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Users className="text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{actividad?.visitantesHoy || 0}</p>
                  <p className="text-xs text-slate-500">Visitantes hoy</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Clock className="text-amber-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{actividad?.entregasPendientes || 0}</p>
                  <p className="text-xs text-slate-500">Entregas pendientes</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Building className="text-emerald-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{actividad?.reservasHoy || 0}</p>
                  <p className="text-xs text-slate-500">Reservas hoy</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rose-500/20">
                  <MessageSquare className="text-rose-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{actividad?.incidenciasPendientes || 0}</p>
                  <p className="text-xs text-slate-500">Incidencias</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Gráficos de Servicios - Fila 2: Comunicación */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">📡 Servicios de Comunicación</h3>
          <p className="text-xs text-slate-400 mb-3">Internet, Teléfono y Gas</p>
          {historicoServicios.datos.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={historicoServicios.datos}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="mes" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} labelStyle={{ color: '#f1f5f9' }} formatter={(value) => formatMoney(value)} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="INTERNET" fill="#8b5cf6" name="Internet" />
                <Bar dataKey="TELEFONO" fill="#06b6d4" name="Teléfono" />
                <Bar dataKey="GAS" fill="#ef4444" name="Gas" />
              </BarChart>
            </ResponsiveContainer>
          ) : (<div className="flex items-center justify-center h-64 text-slate-400"><p className="text-sm">Sin datos</p></div>)}
        </Card>
      </div>

      {/* Departamentos Morosos */}
      <Card className="p-6">
        <h3 className="font-semibold text-white mb-4">Departamentos Morosos</h3>
        {morosos && morosos.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {morosos.map((dpto, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-700/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                    <span className="text-rose-400 font-bold text-sm">{dpto.numero}</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{dpto.propietario}</p>
                    <p className="text-xs text-slate-500">Dpto. {dpto.numero}</p>
                  </div>
                </div>
                <Badge variant="danger">S/ {dpto.monto}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">✓</span>
            </div>
            <p className="text-emerald-400 font-medium">Sin morosidad</p>
            <p className="text-slate-500 text-sm">Todos los departamentos al día</p>
          </div>
        )}
      </Card>

      {/* Sección Mi Departamento - Solo si el admin también es propietario */}
      {usuario?.departamento_id && (
        <Card className="p-5 border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/20">
                <Home className="text-blue-400" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-400">Mi Departamento</p>
                <p className="text-xl font-bold text-white">Dpto. {usuario.departamento_numero}</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              {miDeuda > 0 ? (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Deuda pendiente</p>
                    <p className="text-xl font-bold text-rose-400">{formatMoney(miDeuda)}</p>
                  </div>
                  <Button onClick={() => navigate('/mis-pagos')} icon={Receipt}>
                    Pagar Ahora
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle size={20} />
                  <span className="font-medium">Al día con los pagos</span>
                </div>
              )}

              <Button variant="secondary" onClick={() => navigate('/mi-departamento')} icon={CreditCard}>
                Ver Estado de Cuenta
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

// Componente principal que decide qué dashboard mostrar
const DashboardPage = () => {
  const { usuario, hasPermission } = useAuth();

  // Si es propietario (sin permisos administrativos), mostrar dashboard de propietario
  if (usuario?.rol === 'PROPIETARIO') {
    return <PropietarioDashboard usuario={usuario} />;
  }

  // Para admin, presidente, tesorero, secretario - mostrar dashboard administrativo
  return <AdminDashboard />;
};

export default DashboardPage;